import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Award, Briefcase, BookOpen } from "lucide-react";
import { Chapter, Reveal } from "./Reveal";
import { TUTORS } from "../../data/content";

const TiltImage = ({ src, alt, testid }) => {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rx = useSpring(useTransform(my, [0, 1], [5, -5]), { stiffness: 150, damping: 20 });
    const ry = useSpring(useTransform(mx, [0, 1], [-5, 5]), { stiffness: 150, damping: 20 });

    return (
        <motion.div
            ref={ref}
            data-testid={testid}
            onMouseMove={(e) => {
                const r = ref.current.getBoundingClientRect();
                mx.set((e.clientX - r.left) / r.width);
                my.set((e.clientY - r.top) / r.height);
            }}
            onMouseLeave={() => {
                mx.set(0.5);
                my.set(0.5);
            }}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
            className="relative overflow-hidden bg-cream"
        >
            <img
                src={src}
                alt={alt}
                className="w-full h-[420px] object-cover grayscale hover:grayscale-0 transition-[filter] duration-700"
            />
        </motion.div>
    );
};

const TutorCard = ({ tutor, flip }) => (
    <Reveal>
        <article
            data-testid={`tutor-card-${tutor.id}`}
            className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-start py-16 border-t hairline ${
                flip ? "" : ""
            }`}
        >
            <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                <TiltImage
                    src={tutor.portrait}
                    alt={`Portrait of ${tutor.name}`}
                    testid={`tutor-portrait-${tutor.id}`}
                />
            </div>
            <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                <div className="flex items-baseline gap-4 flex-wrap">
                    <h3 className="font-serif text-3xl md:text-4xl text-ink">{tutor.name}</h3>
                    <span
                        data-testid={`tutor-atar-${tutor.id}`}
                        className="font-mono text-xs tracking-[0.2em] uppercase text-oxblood border border-oxblood/30 px-3 py-1"
                    >
                        ATAR {tutor.atar}
                    </span>
                </div>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate2">
                    {tutor.role} · {tutor.school}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                    {tutor.honours.map((h) => (
                        <span
                            key={h}
                            className="inline-flex items-center gap-1.5 bg-cream px-3 py-1.5 text-xs text-ink"
                        >
                            <Award className="w-3 h-3 text-oxblood" strokeWidth={1.75} />
                            {h}
                        </span>
                    ))}
                </div>

                <p className="mt-6 text-slate2 leading-relaxed text-sm md:text-base max-w-2xl">
                    {tutor.bio}
                </p>

                <div className="mt-6 space-y-1.5">
                    {tutor.experience.map((e) => (
                        <p key={e} className="flex items-start gap-2 text-sm text-slate2">
                            <Briefcase className="w-3.5 h-3.5 mt-0.5 shrink-0 text-navy" strokeWidth={1.75} />
                            {e}
                        </p>
                    ))}
                </div>

                <div className="mt-8">
                    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink mb-4">
                        <BookOpen className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Subjects — {tutor.name.split(" ")[0]}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {tutor.subjects.map((s) => (
                            <span
                                key={s}
                                data-testid={`tutor-subject-${tutor.id}-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                                className="border border-ink/15 px-3 py-1.5 text-xs text-ink hover:border-navy hover:text-navy transition-colors duration-300"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </article>
    </Reveal>
);

export const Tutors = () => (
    <section id="tutors" data-testid="tutors-section" className="py-28 md:py-36 bg-cream/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Chapter
                index="Ch. 05"
                eyebrow="Meet your tutors"
                title={
                    <>
                        Oliver <em className="text-navy">&amp;</em> Ashton.
                    </>
                }
                testid="tutors-heading"
            />
            {TUTORS.map((t, i) => (
                <TutorCard key={t.id} tutor={t} flip={i % 2 === 1} />
            ))}
            <p className="mt-6 text-xs text-slate2/80 max-w-xl">
                *English (Advanced) support focuses on study, essay and exam technique rather
                than text-specific content, due to the recent syllabus change.
            </p>
        </div>
    </section>
);
